/* global document */
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import OutsideClick from '@gorangajic/react-outside-click';
import { Fill } from 'react-slot-fill';
import { ScrollLocky } from 'react-scroll-locky';
import styles from './Modal.css';
import KeyTrap from '../KeyTrap';
import getUniqueId from '../../utils/getUniqueId';
import { trapFocus } from '../../utils/focusUtils';

export const Modal = ({
  onClose,
  children,
  containerStyles,
  className,
  closeOnKeys,
  fill,
  open,
  ariaLabel,
  ariaLabelledBy,
  ...props
}) => {
  const [modalId] = useState(() => getUniqueId('modal'));
  const triggerRef = useRef(null);
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    triggerRef.current = document.activeElement;

    setTimeout(() => {
      if (dialogRef.current) {
        dialogRef.current.focus();
      }
    }, 0);

    const trapCleanup = trapFocus({
      id: modalId,
      initialFocus: false,
    });

    return () => {
      if (typeof trapCleanup === 'function') {
        trapCleanup();
      }
      const node = triggerRef.current;
      if (node && typeof node.focus === 'function') {
        node.focus();
      }
    };
  }, [open, modalId]);

  if (!open && !fill) {
    return null;
  }
  let dialogAriaProps = {};
  if (ariaLabelledBy) {
    dialogAriaProps = { 'aria-labelledby': ariaLabelledBy };
  } else if (ariaLabel) {
    dialogAriaProps = { 'aria-label': ariaLabel };
  }

  const dialogShellProps = {
    className: styles.wrap,
    role: 'dialog',
    tabIndex: '-1',
    'aria-modal': 'true',
    'aria-live': 'assertive',
    ...dialogAriaProps,
  };

  const modal = (
    <ScrollLocky>
      <div {...dialogShellProps} ref={dialogRef}>
        <div id={modalId} className={styles.container}>
          <OutsideClick style={containerStyles} onOutsideClick={onClose}>
            <div {...props} className={className}>
              {children}
            </div>
          </OutsideClick>
          <KeyTrap on={closeOnKeys} handleEvent={onClose} />
        </div>
      </div>
    </ScrollLocky>
  );
  if (fill) {
    return (<Fill name={fill}>
      {open ? modal : null}
    </Fill>);
  }
  return modal;
};

Modal.propTypes = {
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node,
  className: PropTypes.string,
  open: PropTypes.bool.isRequired,
  containerStyles: PropTypes.object,
  closeOnKeys: PropTypes.array,
  fill: PropTypes.string,
  ariaLabel: PropTypes.string,
  ariaLabelledBy: PropTypes.string,
};

Modal.defaultProps = {
  onClose: () => ({}),
  containerStyles: {},
  className: '',
  closeOnKeys: ['esc'],
};

export default Modal;



// WEBPACK FOOTER //
// ./src/client/Components/Modal/Modal.js